import {
  COGNITO_GROUPS,
  FILE_FORMATS,
  OUTPUT_FILES,
  PUBLISH_STATUS,
  S3_BUCKET,
  TEAMS,
} from './const';

// check if the user belongs to any of the existing cognito group
export const isValidUser = (user) => {
  const cognitoGroup =
    user.signInUserSession.accessToken.payload['cognito:groups']?.[0];

  const isAdmin = cognitoGroup === COGNITO_GROUPS.ADMINS;
  const isATeamMember = !![
    TEAMS.ADMIN,
    TEAMS.INCENTIVES,
    TEAMS.PRODUCT_PLANNING,
    TEAMS.RECALL_COMPLIANCE,
  ].includes(user.attributes['custom:team']);

  return isAdmin || isATeamMember;
};

export function downloadFromBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener('click', clickHandler);
    }, 150);
  };
  a.addEventListener('click', clickHandler, false);
  a.click();
  return a;
}

export function downloadFromBase64({ fileData, fileName }) {
  // Convert base64 to byte array
  const byteCharacters = atob(fileData);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  // Create blob and download
  const blob = new Blob([byteArray], {
    type: FILE_FORMATS.EXCEL,
  });

  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName; // Or any other name you want to assign to the downloaded file
  link.click();
}

export const trimString = (str, length) =>
  str.length > length ? `${str.substring(0, length)}...` : str;

export const isEmpty = (obj) =>
  obj && Object.keys(obj).length === 0 && obj.constructor === Object;

export const hasKeys = (obj) =>
  obj && typeof obj === 'object' && Object.keys(obj).length !== 0;

const checkSpecialCharacter = (fileName) => {
  const specialChars = '#%&*:<>?/{|}';
  let error = false;
  // eslint-disable-next-line
  for (let j = 0; j < specialChars.length; j++) {
    if (fileName.indexOf(specialChars[j]) > -1) {
      error = true;
      break;
    }
  }
  return error;
};

export const checkNewFilesForError = (files) => {
  let hasError;
  let errorMsg = '';
  let isInValidFileFormat = false;
  let isInValidFileName = false;
  // check if the selected files are valid
  files.forEach((file) => {
    // check if special characters in file name.
    if (checkSpecialCharacter(file.name)) {
      hasError = true;
      isInValidFileName = true;
    }
    // check if the selected file is an xlsx file
    if (file.type !== FILE_FORMATS.EXCEL) {
      hasError = true;
      isInValidFileFormat = true;
    }
  });

  if (hasError) {
    if (isInValidFileName) {
      errorMsg =
        'The filename may not include any of the following special characters: #%&*:<>?/{|}.';
    }
    // higher precedence for file format check
    if (isInValidFileFormat) {
      errorMsg = 'please select an excel file.';
    }
  }
  return { hasError, errorMsg };
};

export const getModelInfoFromFileKey = (key) => {
  const keyParts = key.split('/');
  const fileName = keyParts.pop(); // Remove file name
  const modelCode = keyParts.pop();
  const modelYear = keyParts.pop();
  return { fileName, modelCode, modelYear };
};

export const delay = (t, v) =>
  new Promise((resolve) => {
    setTimeout(resolve.bind(null, v), t);
  });

export const removeFileExtension = (fileName) => {
  // Find the last occurrence of '.' in the file name
  const lastDotIndex = fileName.lastIndexOf('.');

  // Check if there is a dot in the file name
  if (lastDotIndex !== -1) {
    // Extract the part of the file name before the last dot
    return fileName.slice(0, lastDotIndex);
  }
  // If there is no dot, return the original file name
  return fileName;
};

export const getFileTypeFromOutputFileS3Key = (outputFileS3Key) => {
  if (outputFileS3Key.includes(S3_BUCKET.PCDF_GENERATED_FOLDER)) {
    return OUTPUT_FILES.PCDF;
  }
  if (outputFileS3Key.includes(S3_BUCKET.MVDF_GENERATED_FOLDER)) {
    return OUTPUT_FILES.MVDF;
  }
  if (outputFileS3Key.includes(S3_BUCKET.TAX_IMPORT_GENERATED_FOLDER)) {
    return OUTPUT_FILES.TAX_IMPORT;
  }
  if (outputFileS3Key.includes(S3_BUCKET.VIN_PROCESSED_FOLDER)) {
    return OUTPUT_FILES.VIN;
  }
  return '';
};

export const getPublishLogS3Keys = (file) => {
  const fileType = getFileTypeFromOutputFileS3Key(file.EN?.Key || file.Key);

  if (fileType === OUTPUT_FILES.TAX_IMPORT) {
    return {
      EN: `${
        S3_BUCKET.PUBLISH_HISTORY_FOLDER
      }/${fileType}/${removeFileExtension(file.EN.FileName)}.json`,
      FR: `${
        S3_BUCKET.PUBLISH_HISTORY_FOLDER
      }/${fileType}/${removeFileExtension(file.FR.FileName)}.json`,
    };
  }

  return {
    EN: `${S3_BUCKET.PUBLISH_HISTORY_FOLDER}/${fileType}/${
      file.Metadata.modelyear
    }/${file.Metadata.modelcode}/${removeFileExtension(file.EN.FileName)}.json`,
    FR: `${S3_BUCKET.PUBLISH_HISTORY_FOLDER}/${fileType}/${
      file.Metadata.modelyear
    }/${file.Metadata.modelcode}/${removeFileExtension(file.FR.FileName)}.json`,
  };
};

export const getPriceGuideModelYearAndModelCode = (priceGuide) => {
  const keyParts = priceGuide.dealerMsrp.EN.Key.split('/');
  keyParts.pop();
  keyParts.pop();
  const modelCode = keyParts.pop();
  const modelYear = keyParts.pop();
  return { modelCode, modelYear };
};

const formatPPL = ({ pplFilesByModelYearAndModelCode }) => {
  const pplFilesByModelYear = {};

  Object.keys(pplFilesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(pplFilesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const ppl = pplFilesByModelYearAndModelCode[modelYear][modelCode];
        if (!pplFilesByModelYear[modelYear]) {
          pplFilesByModelYear[modelYear] = [];
        }
        pplFilesByModelYear[modelYear].push(ppl);
      },
    );
  });
  return pplFilesByModelYear;
};

const formatTaxImport = ({ taxImportFile, updatedPublishOverviewFiles }) => {
  let taxImport = {};
  if (hasKeys(taxImportFile)) {
    const enTaxImport = taxImportFile.EN;
    const frTaxImport = taxImportFile.FR;

    const statuses = [enTaxImport.status, frTaxImport.status];
    let status = '';
    if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
      status = PUBLISH_STATUS.PROCESSING;
    } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
      status = PUBLISH_STATUS.REJECTED;
    } else if (
      statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
      statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
    ) {
      status = PUBLISH_STATUS.PUBLISHED;
    }

    taxImport = {
      DisplayName: 'Tax Import',
      FileVersion: enTaxImport.FileVersion,
      EN: enTaxImport,
      FR: frTaxImport,
      shouldUpdate: enTaxImport.shouldUpdate || frTaxImport.shouldUpdate,
      status,
      LastModified: enTaxImport.LastModified,
      Metadata: {
        ...enTaxImport.Metadata,
        uploadedby: enTaxImport.Metadata.username,
      },
    };

    if (taxImport.status === PUBLISH_STATUS.PROCESSING) {
      const isAddedToPublishOverviewFiles = updatedPublishOverviewFiles.some(
        (item) => item?.EN?.Key === taxImport.EN.Key,
      );
      if (!isAddedToPublishOverviewFiles) {
        updatedPublishOverviewFiles.push(taxImport);
      }
    }
  }

  return taxImport;
};

const formatPcdf = ({
  pcdfFilesByModelYearAndModelCode,
  updatedPublishOverviewFiles,
}) => {
  const pcdfsByModelYear = {};

  Object.keys(pcdfFilesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(pcdfFilesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const enPcdf =
          pcdfFilesByModelYearAndModelCode[modelYear][modelCode].EN;
        const frPcdf =
          pcdfFilesByModelYearAndModelCode[modelYear][modelCode].FR;

        const statuses = [enPcdf.status, frPcdf.status];
        let status = '';
        if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
          status = PUBLISH_STATUS.PROCESSING;
        } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
          status = PUBLISH_STATUS.REJECTED;
        } else if (
          statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
          statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
        ) {
          status = PUBLISH_STATUS.PUBLISHED;
        }

        const pcdf = {
          DisplayName: enPcdf.DisplayName,
          FileVersion: enPcdf.FileVersion,
          EN: enPcdf,
          FR: frPcdf,
          shouldUpdate: enPcdf.shouldUpdate || frPcdf.shouldUpdate,
          status,
          LastModified: enPcdf.LastModified,
          Metadata: {
            ...enPcdf.Metadata,
            uploadedby: enPcdf.Metadata.username,
          },
        };

        if (!pcdfsByModelYear[modelYear]) {
          pcdfsByModelYear[modelYear] = [];
        }
        pcdfsByModelYear[modelYear].push(pcdf);

        if (pcdf.status === PUBLISH_STATUS.PROCESSING) {
          const isAddedToPublishOverviewFiles =
            updatedPublishOverviewFiles.some(
              (item) => item?.EN?.Key === pcdf.EN.Key,
            );
          if (!isAddedToPublishOverviewFiles) {
            updatedPublishOverviewFiles.push(pcdf);
          }
        }
      },
    );
  });

  return pcdfsByModelYear;
};

const formatMvdf = ({
  mvdfFilesByModelYearAndModelCode,
  updatedPublishOverviewFiles,
}) => {
  const mvdfsByModelYear = {};

  Object.keys(mvdfFilesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(mvdfFilesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const enMvdf =
          mvdfFilesByModelYearAndModelCode[modelYear][modelCode].EN;
        const frMvdf =
          mvdfFilesByModelYearAndModelCode[modelYear][modelCode].FR;

        const statuses = [enMvdf.status, frMvdf.status];
        let status = '';
        if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
          status = PUBLISH_STATUS.PROCESSING;
        } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
          status = PUBLISH_STATUS.REJECTED;
        } else if (
          statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
          statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
        ) {
          status = PUBLISH_STATUS.PUBLISHED;
        }

        const mvdf = {
          DisplayName: enMvdf.DisplayName,
          FileVersion: enMvdf.FileVersion,
          EN: enMvdf,
          FR: frMvdf,
          shouldUpdate: enMvdf.shouldUpdate || frMvdf.shouldUpdate,
          status,
          LastModified: enMvdf.LastModified,
          Metadata: {
            ...enMvdf.Metadata,
            modelyear: modelYear,
            modelcode: modelCode,
            uploadedby: enMvdf.Metadata.username,
          },
        };

        if (!mvdfsByModelYear[modelYear]) {
          mvdfsByModelYear[modelYear] = [];
        }
        mvdfsByModelYear[modelYear].push(mvdf);

        if (mvdf.status === PUBLISH_STATUS.PROCESSING) {
          const isAddedToPublishOverviewFiles =
            updatedPublishOverviewFiles.some(
              (item) => item?.EN?.Key === mvdf.EN.Key,
            );
          if (!isAddedToPublishOverviewFiles) {
            updatedPublishOverviewFiles.push(mvdf);
          }
        }
      },
    );
  });

  return mvdfsByModelYear;
};

const formatPriceGuide = ({ priceGuidesByModelYearAndModelCode }) => {
  const priceGuidesByModelYear = {};

  Object.keys(priceGuidesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(priceGuidesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const priceGuide =
          priceGuidesByModelYearAndModelCode[modelYear][modelCode];

        const file = {
          ...priceGuide,
          DisplayName: priceGuide?.dealerMsrp?.EN?.DisplayName,
          FileVersion: priceGuide?.dealerMsrp?.EN?.FileVersion,
          LastModified: priceGuide?.dealerMsrp?.EN?.LastModified,
          Metadata: {
            ...priceGuide?.dealerMsrp?.EN?.Metadata,
            modelyear: modelYear,
            modelcode: modelCode,
            uploadedby: priceGuide?.dealerMsrp?.EN?.Metadata.username,
          },
        };

        if (!priceGuidesByModelYear[modelYear]) {
          priceGuidesByModelYear[modelYear] = [];
        }
        priceGuidesByModelYear[modelYear].push(file);
      },
    );
  });

  return priceGuidesByModelYear;
};

const formatVehicleTechSpecs = ({
  vehicleTechSpecsByModelYearAndModelCode,
}) => {
  const vehicleTechSpecsByModelYear = {};

  Object.keys(vehicleTechSpecsByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(vehicleTechSpecsByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const enVehicleTechSpec =
          vehicleTechSpecsByModelYearAndModelCode[modelYear][modelCode].EN;
        const frVehicleTechSpec =
          vehicleTechSpecsByModelYearAndModelCode[modelYear][modelCode].FR;

        const vehicleTechSpec = {
          DisplayName: enVehicleTechSpec.DisplayName,
          FileVersion: enVehicleTechSpec.FileVersion,
          EN: enVehicleTechSpec,
          FR: frVehicleTechSpec,
          shouldUpdate:
            enVehicleTechSpec.shouldUpdate || frVehicleTechSpec.shouldUpdate,
          isUpdating:
            enVehicleTechSpec.isUpdating || frVehicleTechSpec.isUpdating,
          isSelected:
            enVehicleTechSpec.isSelected || frVehicleTechSpec.isSelected,
          LastModified: enVehicleTechSpec.LastModified,
          Metadata: {
            ...enVehicleTechSpec.Metadata,
            modelyear: modelYear,
            modelcode: modelCode,
            uploadedby: enVehicleTechSpec.Metadata.username,
          },
        };

        if (!vehicleTechSpecsByModelYear[modelYear]) {
          vehicleTechSpecsByModelYear[modelYear] = [];
        }
        vehicleTechSpecsByModelYear[modelYear].push(vehicleTechSpec);
      },
    );
  });

  return vehicleTechSpecsByModelYear;
};

const formatTopFeatures = ({ topFeaturesByModelYearAndModelCode }) => {
  const topFeaturesByModelYear = {};

  Object.keys(topFeaturesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(topFeaturesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const topFiveFeatures =
          topFeaturesByModelYearAndModelCode[modelYear][modelCode].topFive;
        const topTenFeatures =
          topFeaturesByModelYearAndModelCode[modelYear][modelCode].topTen;

        const topFeatures = {
          DisplayName: topFiveFeatures.DisplayName,
          FileVersion: topFiveFeatures.FileVersion,
          topFive: topFiveFeatures,
          topTen: topTenFeatures,
          shouldUpdate:
            topFiveFeatures.shouldUpdate || topTenFeatures.shouldUpdate,
          isUpdating: topFiveFeatures.isUpdating || topTenFeatures.isUpdating,
          isSelected: topFiveFeatures.isSelected || topTenFeatures.isSelected,
          LastModified: topFiveFeatures.LastModified,
          Metadata: {
            ...topFiveFeatures.Metadata,
            modelyear: modelYear,
            modelcode: modelCode,
            uploadedby: topFiveFeatures.Metadata.username,
          },
        };

        if (!topFeaturesByModelYear[modelYear]) {
          topFeaturesByModelYear[modelYear] = [];
        }
        topFeaturesByModelYear[modelYear].push(topFeatures);
      },
    );
  });

  return topFeaturesByModelYear;
};

const formatFeaturesGuide = ({ featuresGuidesByModelYearAndModelCode }) => {
  const featuresGuidesByModelYear = {};

  Object.keys(featuresGuidesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(featuresGuidesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const enFeaturesGuide =
          featuresGuidesByModelYearAndModelCode[modelYear][modelCode].EN;
        const frFeaturesGuide =
          featuresGuidesByModelYearAndModelCode[modelYear][modelCode].FR;

        const featuresGuide = {
          DisplayName: enFeaturesGuide.DisplayName,
          FileVersion: enFeaturesGuide.FileVersion,
          EN: enFeaturesGuide,
          FR: frFeaturesGuide,
          shouldUpdate:
            enFeaturesGuide.shouldUpdate || frFeaturesGuide.shouldUpdate,
          isUpdating: enFeaturesGuide.isUpdating || frFeaturesGuide.isUpdating,
          isSelected: enFeaturesGuide.isSelected || frFeaturesGuide.isSelected,
          LastModified: enFeaturesGuide.LastModified,
          Metadata: {
            ...enFeaturesGuide.Metadata,
            modelyear: modelYear,
            modelcode: modelCode,
            uploadedby: enFeaturesGuide.Metadata.username,
          },
        };

        if (!featuresGuidesByModelYear[modelYear]) {
          featuresGuidesByModelYear[modelYear] = [];
        }
        featuresGuidesByModelYear[modelYear].push(featuresGuide);
      },
    );
  });

  return featuresGuidesByModelYear;
};

export const formatIncentivesFiles = ({
  pplFilesByModelYearAndModelCode,
  taxImportFile,
  pcdfFilesByModelYearAndModelCode,
  publishOverviewFiles,
}) => {
  const updatedPublishOverviewFiles = [...publishOverviewFiles];

  const pplFilesByModelYear = formatPPL({ pplFilesByModelYearAndModelCode });
  const taxImport = formatTaxImport({
    taxImportFile,
    updatedPublishOverviewFiles,
  });
  const pcdfsByModelYear = formatPcdf({
    pcdfFilesByModelYearAndModelCode,
    updatedPublishOverviewFiles,
  });

  return {
    pplFilesByModelYear,
    taxImport,
    pcdfsByModelYear,
    updatedPublishOverviewFiles,
  };
};

export const formatProductPlanningFiles = ({
  pplFilesByModelYearAndModelCode,
  mvdfFilesByModelYearAndModelCode,
  priceGuidesByModelYearAndModelCode,
  vehicleTechSpecsByModelYearAndModelCode,
  topFeaturesByModelYearAndModelCode,
  featuresGuidesByModelYearAndModelCode,
  publishOverviewFiles,
}) => {
  const updatedPublishOverviewFiles = [...publishOverviewFiles];

  const pplFilesByModelYear = formatPPL({ pplFilesByModelYearAndModelCode });
  const mvdfsByModelYear = formatMvdf({
    mvdfFilesByModelYearAndModelCode,
    updatedPublishOverviewFiles,
  });
  const priceGuidesByModelYear = formatPriceGuide({
    priceGuidesByModelYearAndModelCode,
  });
  const vehicleTechSpecsByModelYear = formatVehicleTechSpecs({
    vehicleTechSpecsByModelYearAndModelCode,
  });
  const topFeaturesByModelYear = formatTopFeatures({
    topFeaturesByModelYearAndModelCode,
  });

  const featuresGuidesByModelYear = formatFeaturesGuide({
    featuresGuidesByModelYearAndModelCode,
  });

  return {
    pplFilesByModelYear,
    mvdfsByModelYear,
    priceGuidesByModelYear,
    vehicleTechSpecsByModelYear,
    topFeaturesByModelYear,
    featuresGuidesByModelYear,
    updatedPublishOverviewFiles,
  };
};
